import React, { useState } from 'react';
import { HiX } from 'react-icons/hi';
import { TemplateVariable } from '../../../../../types/communication';
import { variableToken } from '../../../../../utils/templateVariables';
import VariablePicker from './VariablePicker';

interface TokenListFieldProps {
    id: string;
    label: string;
    values: string[];
    onChange: (values: string[]) => void;
    variables: TemplateVariable[];
    placeholder?: string;
    hint?: string;
    validate?: (value: string) => string | null;
    max?: number;
    disabled?: boolean;
    error?: string;
}

const isToken = (value: string) => /^\{\{.+\}\}$/.test(value.trim());

const TokenListField: React.FC<TokenListFieldProps> = ({
    id,
    label,
    values,
    onChange,
    variables,
    placeholder = 'Digite e pressione Enter',
    hint,
    validate,
    max,
    disabled,
    error,
}) => {
    const [draft, setDraft] = useState('');
    const [localError, setLocalError] = useState<string | null>(null);
    const full = max !== undefined && values.length >= max;

    const add = (raw: string) => {
        const value = raw.trim().replace(/,$/, '');
        if (!value) return;
        if (values.includes(value)) {
            setDraft('');
            return;
        }
        const problem = !isToken(value) && validate ? validate(value) : null;
        if (problem) {
            setLocalError(problem);
            return;
        }
        onChange([...values, value]);
        setDraft('');
        setLocalError(null);
    };

    const remove = (value: string) => onChange(values.filter((v) => v !== value));

    const labelFor = (value: string) => value;

    const shownError = localError ?? error;

    return (
        <div>
            <div className="flex items-end justify-between mb-1 gap-2">
                <label htmlFor={id} className="text-xs font-medium text-gray-600 dark:text-gray-300">
                    {label}
                </label>
                {!disabled && !full && (
                    <VariablePicker variables={variables} onPick={(name) => add(variableToken(name))} compact />
                )}
            </div>
            <div
                className={`flex flex-wrap items-center gap-1.5 rounded-lg border bg-white dark:bg-slate-900 px-2 py-1.5 min-h-[40px] ${
                    shownError ? 'border-red-400' : 'border-gray-300 dark:border-slate-600 focus-within:border-teal-500'
                } ${disabled ? 'bg-slate-50 dark:bg-slate-800' : ''}`}
            >
                {values.map((value) => (
                    <span
                        key={value}
                        title={value}
                        className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs ${
                            isToken(value)
                                ? 'bg-teal-50 text-teal-800 border border-teal-200 dark:bg-teal-900/40 dark:text-teal-200 dark:border-teal-800'
                                : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-100'
                        }`}
                    >
                        {labelFor(value)}
                        {!disabled && (
                            <button type="button" onClick={() => remove(value)} className="opacity-60 hover:opacity-100" aria-label={`Remover ${value}`}>
                                <HiX size={12} />
                            </button>
                        )}
                    </span>
                ))}
                {!disabled && !full && (
                    <input
                        id={id}
                        value={draft}
                        placeholder={values.length ? '' : placeholder}
                        onChange={(e) => {
                            setDraft(e.target.value);
                            if (localError) setLocalError(null);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
                                if (draft.trim()) {
                                    e.preventDefault();
                                    add(draft);
                                }
                            } else if (e.key === 'Backspace' && !draft && values.length) {
                                remove(values[values.length - 1]);
                            }
                        }}
                        onBlur={() => add(draft)}
                        className="flex-1 min-w-[140px] bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none py-0.5"
                    />
                )}
                {disabled && !values.length && <span className="text-sm text-gray-400">—</span>}
            </div>
            {shownError ? (
                <p className="text-[11px] text-red-500 mt-1">{shownError}</p>
            ) : (
                hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>
            )}
        </div>
    );
};

export default TokenListField;
